import { Icon, notification } from 'antd'
import axios, { AxiosInstance } from 'axios'
import _ from 'lodash'
import * as React from 'react'
import qs from 'query-string'

import configurations from '../utils/config/configurations'
import i18n from '../utils/config/i18n'

let ufodriveServerPublicApi: AxiosInstance | null = null
let ufodriveServerApi: AxiosInstance | null = null

export const createConnectionApi = () => {
  const lng = i18n.language.length > 2 ? i18n.language.substring(0, 2) : i18n.language
  ufodriveServerPublicApi = axios.create({
    headers: { 'Accept-Language': lng },
    baseURL: configurations.UFO_SERVER_PUBLIC_API_URL,
    timeout: 30000
  })
  ufodriveServerApi = axios.create({
    headers: { 'Accept-Language': lng },
    baseURL: configurations.UFO_SERVER_PRIVATE_API_URL,
    timeout: 30000
  })
}

export const injectTokenInApi = (apiToken: string) => {
  if (ufodriveServerApi) {
    ufodriveServerApi.defaults.headers.common.Authorization =
      'Bearer ' + apiToken
    ufodriveServerApi.defaults.headers.post.Authorization = 'Bearer ' + apiToken
  }
}

export const injectLanguageInApi = (language: string) => {
  if (ufodriveServerApi && language) {
    const lng = language.length > 2 ? language.substring(0, 2) : language
    ufodriveServerApi.defaults.headers.common['Accept-Language'] = lng
    ufodriveServerApi.defaults.headers.post['Accept-Language'] = lng
  }
}

export const injectCountryCode = (countryCode: string) => {
  if (ufodriveServerApi) {
    ufodriveServerApi.defaults.headers['x-country'] = countryCode
  }
}

export async function getFromExternalApi(
  path: string,
  queries?: any,
  suppressToastBox = false
): Promise<any> {
  try {
    const pathAndQuery = `${path}${queries ? '?' + qs.stringify(queries) : ''}`

    return await axios.get(pathAndQuery)
  } catch (error) {
    handleError(error, suppressToastBox)

    return error
  }
}

/**
 * GET a path relative to API root url.
 * @param {String}  path Relative path to the configured API endpoint
 * @param {Boolean} suppressToastBox If true, no warning is shown on failed request
 * @param {Boolean} usePublicApi If true, use th epublic api
 * @returns {Promise} of response body
 */
export async function getFromApi(
  path: string,
  queries?: any,
  suppressToastBox = false,
  usePublicApi = false
): Promise<any> {
  const api = usePublicApi ? ufodriveServerPublicApi : ufodriveServerApi

  try {
    if (!api) {
      throw new Error('api is required')
    }

    const pathAndQuery = `${path}${queries ? '?' + qs.stringify(queries) : ''}`

    return await api.get(pathAndQuery)
  } catch (error) {
    handleError(error, suppressToastBox)

    return error
  }
}

/**
 * POST JSON to a path relative to API root url
 * @param {String} path Relative path to the configured API endpoint
 * @param {Record<string, unknown>} body Anything that you can pass to JSON.stringify
 * @param {Boolean} suppressToastBox If true, no warning is shown on failed request
 * @param {Boolean} usePublicApi If true, use th epublic api
 * @returns {Promise}  of response body
 */
export async function postToApi(
  path: string,
  body: Record<string, unknown>,
  suppressToastBox = false,
  usePublicApi = false
): Promise<any> {
  const api = usePublicApi ? ufodriveServerPublicApi : ufodriveServerApi

  try {
    if (!api) {
      throw new Error('api is required')
    }

    return await api.post(path, body)
  } catch (error) {
    handleError(error, suppressToastBox)

    return error
  }
}

/**
 * PUT JSON to a path relative to API root url
 * @param {String} path Relative path to the configured API endpoint
 * @param {Record<string, unknown>} body Anything that you can pass to JSON.stringify
 * @param {Boolean} suppressToastBox If true, no warning is shown on failed request
 * @param {Boolean} usePublicApi If true, use th epublic api
 * @returns {Promise} of response body
 */
export async function putToApi(
  path: string,
  body: Record<string, unknown>,
  suppressToastBox = false,
  usePublicApi = false
): Promise<any> {
  const api = usePublicApi ? ufodriveServerPublicApi : ufodriveServerApi

  try {
    if (!api) {
      throw new Error('api is required')
    }

    return await api.put(path, body)
  } catch (error) {
    handleError(error, suppressToastBox)

    return error
  }
}

async function handleError(error: any, suppressToastBox: boolean) {
  const ufoError = formatApiError(error)
  console.error('handleApiError', error)

  if (!suppressToastBox) {
    await showToastError(ufoError.message)
  }
}

function formatApiError(error: any) {
  let key = 'error:api'
  let message = error.message

  if (_.get(error, 'response.data.message')) {
    message = error.response.data.message
    key = error.response.status || 'api'
  }

  return { key, message }
}

export async function showToastError(message: string) {
  await notification.error({
    message,
    description: null,
    duration: 20,
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    icon: React.createElement(Icon, { type: 'exclamation-circle' }),
    style: { background: '#ff5630' }
  })
}

export const urlEncode = (data: Record<string, string | number | boolean>) => {
  const ret = []

  for (const i in data) {
    if (data[i] !== undefined) {
      ret.push(encodeURIComponent(i) + '=' + encodeURIComponent(data[i]))
    }
  }

  return ret.join('&')
}

export const urlParams = (
  url: string,
  params: Record<string, string | number | boolean>
) => {
  if (_.isEmpty(params)) {
    return url
  }

  const delimiter = url.indexOf('?') === -1 ? '?' : '&'

  return `${url}${delimiter}${urlEncode(params)}`
}
